<!-- STEP 1 – How would you like to generate test cases? -->
<ng-container *ngIf="currentStep === 1">
  <div class="tcgm-container">
    <div class="tcgm-body">
      <h2 class="tcgm-title">How would you like to generate test cases?</h2>
      <div class="tcgm-options">
        <button
          type="button"
          class="tcgm-option"
          [class.tcgm-option--selected]="selectedSource === 'user_stories'"
          [attr.aria-pressed]="selectedSource === 'user_stories'"
          (click)="selectSource('user_stories')"
        >
          <span class="tcgm-option-title">From User Stories</span>
          <p class="tcgm-option-desc">
            Transform your user stories (<strong>Jira, Rally, Azure DevOps, PDF, XLSX, DOCX, and S3</strong>)
            into comprehensive test cases.
          </p>
        </button>
        <button
          type="button"
          class="tcgm-option"
          [class.tcgm-option--selected]="selectedSource === 'other_documents'"
          [attr.aria-pressed]="selectedSource === 'other_documents'"
          (click)="selectSource('other_documents')"
        >
          <span class="tcgm-option-title">From other documents</span>
          <p class="tcgm-option-desc">
            Turn your existing product documentation (<strong>FSD, BRD, Figma designs, and Swagger API</strong>)
            into structured requirements, then generate comprehensive test cases.
          </p>
        </button>
      </div>
    </div>
    <div class="tcgm-footer">
      <span class="tcgm-step-label">{{ stepLabel }}</span>
      <div class="tcgm-actions">
        <button type="button" class="tcgm-btn-cancel" (click)="onCancel()">Cancel</button>
        <button type="button" class="tcgm-btn-continue" (click)="onContinue()">Continue</button>
      </div>
    </div>
  </div>
</ng-container>

<!-- STEP 2 – Extract User Stories (user_stories flow) -->
<ng-container *ngIf="currentStep === 2 && !isOtherDocuments">
  <div class="tcgm-s2-container">

    <div class="tcgm-s2-header">
      <h2 class="tcgm-s2-title">Extract User Stories</h2>
      <button
        type="button"
        class="tcgm-s2-close"
        aria-label="Close"
        [disabled]="isAppContextProgress"
        (click)="onCancel()"
      >
        <span class="tcgm-s2-close-icon" aria-hidden="true">&#x2715;</span>
      </button>
    </div>

    <div class="tcgm-s2-body">

      <!-- Info banner -->
      <div class="tcgm-s2-banner">
        <img
          class="tcgm-s2-banner-icon"
          src="assets/images/virtusa-icons/recomendation.svg"
          alt=""
          aria-hidden="true"
          width="30"
          height="29"
        />
        <div class="tcgm-s2-banner-content">
          <span class="tcgm-s2-banner-title">Recommended Actions</span>
          <p class="tcgm-extract-banner-text">
            Extraction sources or files are managed in
            <strong>Project Settings &#x2192; Connections</strong>. Upload or configure your source files there to make them available for extraction.
          </p>
        </div>
      </div>

      <!-- Tab selector: Extract User Stories From -->
      <div class="tcgm-extract-field">
        <span class="tcgm-s2-label">Extract User Stories From <span class="tcgm-required">*</span></span>
        <div class="tcgm-extract-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="tcgm-extract-tab"
            [class.tcgm-extract-tab--active]="extractTab === 'alm'"
            [attr.aria-selected]="extractTab === 'alm'"
            (click)="switchExtractTab('alm')"
          >
            <span class="tcgm-extract-tab-label">ALM connection</span>
            <span class="tcgm-extract-tab-sub">Jira/Rally/Azure DevOps</span>
          </button>
          <button
            type="button"
            role="tab"
            class="tcgm-extract-tab"
            [class.tcgm-extract-tab--active]="extractTab === 'requirement_doc'"
            [attr.aria-selected]="extractTab === 'requirement_doc'"
            (click)="switchExtractTab('requirement_doc')"
          >
            <span class="tcgm-extract-tab-label">Requirement Document</span>
            <span class="tcgm-extract-tab-sub">PDF/XLSX/DOCX</span>
          </button>
          <button
            type="button"
            role="tab"
            class="tcgm-extract-tab"
            [class.tcgm-extract-tab--active]="extractTab === 'other_sources'"
            [attr.aria-selected]="extractTab === 'other_sources'"
            (click)="switchExtractTab('other_sources')"
          >
            <span class="tcgm-extract-tab-label">Other Sources</span>
            <span class="tcgm-extract-tab-sub">S3</span>
          </button>
        </div>
      </div>

      <!-- ── ALM Connection tab ── -->
      <ng-container *ngIf="extractTab === 'alm'">

        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="almProvider">
            Connection Provider <span class="tcgm-required">*</span>
          </label>
          <div class="tcgm-s2-select-wrap">
            <select id="almProvider" class="tcgm-s2-select" [(ngModel)]="almProvider"
              name="almProvider" (ngModelChange)="onAlmProviderChange()">
              <option value="" disabled>
                {{ isLoadingConnections ? 'Loading...' : 'Select provider' }}
              </option>
              <option *ngFor="let p of almProviderOptions" [value]="p">{{ p }}</option>
            </select>
            <span class="tcgm-s2-select-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>

        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="almConnName">
            Connection Name <span class="tcgm-required">*</span>
          </label>
          <div class="tcgm-s2-select-wrap">
            <select id="almConnName" class="tcgm-s2-select" [(ngModel)]="almConnectionName" name="almConnectionName">
              <option value="" disabled>
                {{ isLoadingConnections ? 'Loading...' : 'Select connection name' }}
              </option>
              <option *ngFor="let c of almConnectionOptions" [value]="c">{{ c }}</option>
            </select>
            <span class="tcgm-s2-select-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>

        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="almExtractFrom">
            Extract From
          </label>
          <nz-date-picker
            id="almExtractFrom"
            class="tcgm-date-picker"
            [(ngModel)]="almExtractFrom"
            name="almExtractFrom"
            nzPlaceHolder="Select date"
            nzFormat="yyyy-MM-dd"
            [nzAllowClear]="true"
            [nzDisabledDate]="disableFutureDates"
          ></nz-date-picker>
        </div>

      </ng-container>

      <!-- ── Requirement Document tab ── -->
      <ng-container *ngIf="extractTab === 'requirement_doc'">
        <div class="tcgm-reqDoc-layout">

          <!-- Left: direct file upload -->
          <div class="tcgm-reqDoc-upload-side">
            <div class="tcgm-reqDoc-zone"
              [class.tcgm-reqDoc-zone--has-file]="!!usReqDocName"
              (click)="triggerFileInput(usDocFileInput)"
              (keydown.enter)="triggerFileInput(usDocFileInput)"
              (keydown.space)="triggerFileInput(usDocFileInput)"
              role="button" tabindex="0" aria-label="Upload requirement document">
              <input #usDocFileInput id="usDocFileNew" type="file" class="tcgm-s2-upload-hidden"
                [accept]="usReqDocAcceptFormats" (change)="onUsNewDocFileChange($event)"/>
              <ng-container *ngIf="!usReqDocName">
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 30V16" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M15 23L22 16L29 23" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 34H31" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
                <span class="tcgm-reqDoc-choose">Choose File</span>
                <span class="tcgm-reqDoc-hint">Only .PDF .XLSX &amp; .DOCX</span>
              </ng-container>
              <ng-container *ngIf="usReqDocName">
                <img [src]="'assets/images/' + usReqDocIconName + '.svg'" width="36" height="36" [alt]="usReqDocType" />
                <span class="tcgm-reqDoc-filename">{{ usReqDocName }}</span>
                <button type="button" class="tcgm-reqDoc-clear"
                  (click)="clearUsReqDoc($event)" aria-label="Remove file">&#10005;</button>
              </ng-container>
            </div>
          </div>

          <!-- Vertical divider -->
          <div class="tcgm-reqDoc-divider"></div>

          <!-- Right: recommended template downloads -->
          <div class="tcgm-reqDoc-templates-side">
            <div class="tcgm-reqDoc-rec-badge">Recommended</div>
            <p class="tcgm-reqDoc-rec-desc">
              You can download the attached template and use them as a starting point for your own file.
            </p>
            <div class="tcgm-reqDoc-template-list">
              <div *ngFor="let file of reqDocSampleFiles" class="tcgm-reqDoc-template-row">
                <div class="tcgm-reqDoc-template-info">
                  <img [src]="file.icon" [alt]="file.type" width="20" height="20" />
                  <span class="tcgm-reqDoc-template-name">{{ file.name }}</span>
                </div>
                <ng-container *ngIf="!file.loading; else reqDocDownloadSpinner">
                  <button type="button" class="tcgm-reqDoc-download-btn"
                    (click)="downloadReqDocTemplate(file.type)" [attr.aria-label]="'Download ' + file.name">
                    <img src="assets/images/virtusa-icons/download-icon.svg" alt="Download" width="18" height="18" />
                  </button>
                </ng-container>
                <ng-template #reqDocDownloadSpinner>
                  <span class="tcgm-upload-spinner" aria-label="Loading"></span>
                </ng-template>
              </div>
            </div>
          </div>

        </div>
      </ng-container>

      <!-- ── Other Sources tab ── -->
      <ng-container *ngIf="extractTab === 'other_sources'">

        <ng-container *ngIf="!otherIsAddingNewConn">
          <div class="tcgm-s2-field">
            <label class="tcgm-s2-label" for="otherConnType">
              Connection Type <span class="tcgm-required">*</span>
            </label>
            <div class="tcgm-s2-select-wrap">
              <select id="otherConnType" class="tcgm-s2-select"
                [(ngModel)]="otherConnType" name="otherConnType"
                (ngModelChange)="onOtherConnTypeChange()">
                <option value="" disabled>Select connection type</option>
                <option *ngFor="let t of otherConnTypeOptions" [value]="t">{{ t }}</option>
              </select>
              <span class="tcgm-s2-select-arrow" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>

          <div class="tcgm-s2-field">
            <label class="tcgm-s2-label" for="otherConnName">
              Connection Name <span class="tcgm-required">*</span>
            </label>
            <div class="tcgm-s2-select-wrap">
              <select id="otherConnName" class="tcgm-s2-select" [(ngModel)]="otherConnName" name="otherConnName">
                <option value="" disabled>
                  {{ isLoadingConnections ? 'Loading...' : 'Select connection name' }}
                </option>
                <option *ngFor="let c of otherConnNameOptions" [value]="c">{{ c }}</option>
              </select>
              <span class="tcgm-s2-select-arrow" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </div>

          <button type="button" class="tcgm-s2-add-doc-link" (click)="onOtherAddNewConnection()">
            + Add a new connection
          </button>
        </ng-container>

        <!-- Add-new connection card -->
        <div *ngIf="otherIsAddingNewConn" class="tcgm-add-doc-card">
          <div class="tcgm-s2-field">
            <div class="tcgm-s2-label-row">
              <label class="tcgm-s2-label" for="otherConnFileNew">
                File <span class="tcgm-required">*</span>
              </label>
              <span class="tcgm-s2-validation-icons">
                <button type="button" class="tcgm-s2-valid-icon"
                  [class.visible]="isOtherNewConnFormValid && !isUploadingOtherConn"
                  [disabled]="!isOtherNewConnFormValid || isUploadingOtherConn"
                  aria-label="Confirm and upload file" (click)="onOtherConfirmConnection()">&#10003;</button>
                <span *ngIf="isUploadingOtherConn" class="tcgm-upload-spinner" aria-label="Uploading..."></span>
                <button type="button" class="tcgm-s2-invalid-icon visible"
                  [disabled]="isUploadingOtherConn"
                  aria-label="Cancel" (click)="otherIsAddingNewConn = false; otherUploadError = ''">&#10005;</button>
              </span>
            </div>
            <div class="tcgm-s2-upload-zone"
              [class.tcgm-s2-upload-zone--has-file]="!!otherNewConnFile && !isUploadingOtherConn"
              (click)="!isUploadingOtherConn && triggerFileInput(otherConnFileInput)"
              (keydown.enter)="!isUploadingOtherConn && triggerFileInput(otherConnFileInput)"
              (keydown.space)="!isUploadingOtherConn && triggerFileInput(otherConnFileInput)"
              role="button" tabindex="0" aria-label="Upload file">
              <input #otherConnFileInput id="otherConnFileNew" type="file" class="tcgm-s2-upload-hidden"
                [accept]="otherSourceAcceptFormats" [disabled]="isUploadingOtherConn"
                (change)="onOtherNewConnFileChange($event)"/>
              <ng-container *ngIf="!otherNewConnFile && !isUploadingOtherConn">
                <span class="tcgm-s2-upload-plus" aria-hidden="true">+</span>
                <span class="tcgm-s2-upload-text">{{ otherSourceUploadHint }}</span>
              </ng-container>
              <ng-container *ngIf="isUploadingOtherConn">
                <span class="tcgm-upload-spinner tcgm-upload-spinner--lg" aria-hidden="true"></span>
                <span class="tcgm-s2-upload-text">Uploading...</span>
              </ng-container>
              <span *ngIf="otherNewConnFile && !isUploadingOtherConn" class="tcgm-s2-upload-filename">{{ otherNewConnFile.name }}</span>
            </div>
            <p *ngIf="otherUploadError" class="tcgm-upload-error" role="alert">{{ otherUploadError }}</p>
          </div>
        </div>

      </ng-container>

      <div class="divider"></div>

      <!-- ── Application Context (all tabs) — same BE flow as app-user-stories ── -->
      <app-feature-toggle-card
        title="Add Application Context (optional)"
        description="Include additional metadata from the application environment to provide deeper context, helping generate more accurate requirements and test cases"
        [enabled]="addAppContext"
        (enabledChange)="onAppContextToggle($event)"
      >
        <!-- Processing -->
        <div *ngIf="isAppContextProgress" class="tcgm-appctx-progress" aria-live="polite">
          <app-progress-bar
            [text]="appContextProgressText"
            [progress]="appContextProgressPct"
            [isError]="isAppContextProgressError"
          ></app-progress-bar>
        </div>

        <!-- Already uploaded / ready -->
        <div
          *ngIf="!isAppContextProgress && uploadedApplicationContext?.originalFileName"
          class="tcgm-appctx-ready"
        >
          <div class="tcgm-appctx-file-chip">
            <img [src]="uploadedAppContextIcon" width="22" height="22" alt="" />
            <span class="tcgm-appctx-file-name" [title]="uploadedAppContextFileName">
              {{ uploadedAppContextFileName }}
            </span>
            <button
              type="button"
              class="tcgm-appctx-remove"
              [disabled]="isDeletingAppContext"
              (click)="deleteUploadedAppContext()"
              aria-label="Remove application context"
            >
              <img src="assets/images/virtusa-icons/delete.svg" alt="" width="16" height="16" />
            </button>
          </div>
          <p class="tcgm-appctx-ready-msg">
            Application context is ready and will be used to improve extraction quality.
          </p>
          <p *ngIf="appContextError" class="tcgm-upload-error" role="alert">{{ appContextError }}</p>
        </div>

        <!-- Upload / select when no context yet -->
        <ng-container *ngIf="!isAppContextProgress && !uploadedApplicationContext?.originalFileName">
          <div class="tcgm-appctx-upload-panel">
            <div
              class="tcgm-appctx-dropzone"
              [class.tcgm-appctx-dropzone--has-file]="!!newAppContextFile"
              (click)="triggerAppContextFileInput(appCtxFileInput)"
              (keydown.enter)="triggerAppContextFileInput(appCtxFileInput)"
              (keydown.space)="triggerAppContextFileInput(appCtxFileInput)"
              role="button"
              tabindex="0"
              aria-label="Upload application context file"
            >
              <input
                #appCtxFileInput
                id="appCtxFileNew"
                type="file"
                class="tcgm-s2-upload-hidden"
                [accept]="appContextAcceptFormats"
                (change)="onNewAppContextFileChange($event)"
              />
              <ng-container *ngIf="!newAppContextFile">
                <svg width="36" height="36" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M22 30V16" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M15 23L22 16L29 23" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M13 34H31" stroke="#7B5CF0" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
                <span class="tcgm-appctx-drop-title">
                  Drag &amp; drop your file here or <span class="tcgm-appctx-choose">Choose File</span>
                </span>
                <span class="tcgm-appctx-drop-hint">{{ appContextUploadHint }}</span>
              </ng-container>
              <ng-container *ngIf="newAppContextFile">
                <img
                  [src]="newAppContextFile.name.toLowerCase().endsWith('.pdf') ? 'assets/images/pdf.svg' : 'assets/images/docx.svg'"
                  width="28"
                  height="28"
                  alt=""
                />
                <span class="tcgm-appctx-file-name">{{ newAppContextFile.name }}</span>
                <button
                  type="button"
                  class="tcgm-appctx-clear-file"
                  (click)="clearNewAppContextFile($event)"
                  aria-label="Remove selected file"
                >&#10005;</button>
              </ng-container>
            </div>

            <button
              type="button"
              class="tcgm-appctx-upload-btn"
              [disabled]="!canUploadAppContext"
              (click)="uploadAppContext()"
            >
              Upload Application Context
            </button>
          </div>

          <ng-container *ngIf="appContextOptions.length">
            <div class="tcgm-appctx-divider" role="separator">
              <span>or choose an existing file</span>
            </div>

            <div class="tcgm-s2-field tcgm-appctx-existing">
              <label class="tcgm-s2-label" for="appCtxSelect">Existing Application Context</label>
              <div class="tcgm-appctx-existing-row">
                <div class="tcgm-s2-select-wrap tcgm-appctx-existing-select">
                  <select
                    id="appCtxSelect"
                    class="tcgm-s2-select"
                    [(ngModel)]="appContextValue"
                    name="appContextValue"
                  >
                    <option value="" disabled>
                      {{ isLoadingConnections ? 'Loading...' : 'Select a PDF or DOCX file' }}
                    </option>
                    <option *ngFor="let ctx of appContextOptions" [value]="ctx.id">{{ ctx.name }}</option>
                  </select>
                  <span class="tcgm-s2-select-arrow" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
                <button
                  type="button"
                  class="tcgm-appctx-apply-btn"
                  [disabled]="!canApplyExistingAppContext"
                  (click)="applyExistingAppContext()"
                >
                  Use Selected
                </button>
              </div>
            </div>
          </ng-container>

          <p *ngIf="appContextError" class="tcgm-upload-error" role="alert">{{ appContextError }}</p>
        </ng-container>
      </app-feature-toggle-card>

    </div>

    <p class="tcgm-extract-error" *ngIf="extractStartError">{{ extractStartError }}</p>

    <div class="tcgm-footer">
      <span class="tcgm-step-label">{{ stepLabel }}</span>
      <div class="tcgm-actions">
        <button type="button" class="tcgm-btn-cancel" (click)="onBack()" [disabled]="isStartingExtract || isAppContextProgress">Back</button>
        <button
          type="button"
          class="tcgm-btn-continue"
          [disabled]="!isExtractStepValid || usIsAddingNewDoc || otherIsAddingNewConn || isStartingExtract || isAppContextProgress || isUploadingOtherConn"
          (click)="onExtract()"
        >{{ isStartingExtract ? 'Starting…' : 'Extract' }}</button>
      </div>
    </div>

  </div>
</ng-container>

<!-- STEP 2 – Which document would you like to use? (other_documents only) -->
<ng-container *ngIf="currentStep === 2 && isOtherDocuments">
  <div class="tcgm-s2-container">

    <div class="tcgm-s2-header">
      <h2 class="tcgm-s2-title">Which document would you like to use to generate test cases?</h2>
      <button type="button" class="tcgm-s2-close" aria-label="Close" (click)="onCancel()">
        <span class="tcgm-s2-close-icon" aria-hidden="true">&#x2715;</span>
      </button>
    </div>

    <div class="tcgm-s2-body">

      <!-- Recommended Actions banner -->
      <div class="tcgm-s2-banner">
        <img
          class="tcgm-s2-banner-icon"
          src="assets/images/virtusa-icons/recomendation.svg"
          alt=""
          aria-hidden="true"
          width="30"
          height="29"
        />
        <div class="tcgm-s2-banner-content">
          <span class="tcgm-s2-banner-title">Recommended Actions</span>
          <ul class="tcgm-s2-banner-list">
            <li>Uploaded source files are available here.</li>
            <li>To add a single document, click <strong>+ Add a New Document.</strong></li>
            <li>For multiple document uploads, go to <strong>Project Settings &#x2192; Storage Connections.</strong></li>
          </ul>
        </div>
      </div>

      <!-- Browse mode: dropdowns -->
      <ng-container *ngIf="!isAddingNewDocument">

        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="tcgDocTypeBrowse">
            Document Type <span class="tcgm-required">*</span>
          </label>
          <div class="tcgm-s2-select-wrap">
            <select
              id="tcgDocTypeBrowse"
              class="tcgm-s2-select"
              [(ngModel)]="documentType"
              name="documentType"
              (ngModelChange)="onDocumentTypeChange()"
            >
              <option value="" disabled>
                {{ isLoadingConnections ? 'Loading...' : 'Select source document type' }}
              </option>
              <option *ngFor="let t of documentTypeOptions" [value]="t.value" [disabled]="!!t.disabled">{{ t.value }}</option>
            </select>
            <span class="tcgm-s2-select-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>

        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="tcgDocNameBrowse">
            Document Name <span class="tcgm-required">*</span>
          </label>
          <div class="tcgm-s2-select-wrap">
            <select
              id="tcgDocNameBrowse"
              class="tcgm-s2-select"
              [(ngModel)]="documentName"
              name="documentName"
            >
              <option value="" disabled>
                {{ isLoadingConnections ? 'Loading...' : 'Select source document name' }}
              </option>
              <option *ngFor="let d of documentNameOptions" [value]="d">{{ d }}</option>
            </select>
            <span class="tcgm-s2-select-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>

        <button type="button" class="tcgm-s2-add-doc-link" (click)="onAddNewDocument()">
          + Add a new document
        </button>

      </ng-container>

      <!-- Add-new mode: highlighted card with inline form -->
      <div *ngIf="isAddingNewDocument" class="tcgm-add-doc-card">

        <!-- Document Type with confirm (✓) / discard (✗) action buttons -->
        <div class="tcgm-s2-field">
          <div class="tcgm-s2-label-row">
            <label class="tcgm-s2-label" for="tcgDocTypeNew">
              Document Type <span class="tcgm-required">*</span>
            </label>
            <span class="tcgm-s2-validation-icons">
              <button
                type="button"
                class="tcgm-s2-valid-icon"
                [class.visible]="isNewDocumentFormValid && !isUploadingDocument"
                aria-label="Upload and save document"
                [disabled]="!isNewDocumentFormValid || isUploadingDocument"
                (click)="onConfirmDocument()"
              >&#10003;</button>
              <span *ngIf="isUploadingDocument" class="tcgm-upload-spinner" aria-label="Uploading..."></span>
              <button
                type="button"
                class="tcgm-s2-invalid-icon visible"
                aria-label="Cancel adding document"
                [disabled]="isUploadingDocument"
                (click)="isAddingNewDocument = false"
              >&#10005;</button>
            </span>
          </div>
          <div class="tcgm-s2-select-wrap">
            <select
              id="tcgDocTypeNew"
              class="tcgm-s2-select"
              [(ngModel)]="documentType"
              name="documentTypeNew"
              (blur)="onDocumentTypeBlur()"
            >
              <option value="" disabled>Select document type</option>
              <option *ngFor="let t of documentTypeOptions" [value]="t.value" [disabled]="!!t.disabled">{{ t.value }}</option>
            </select>
            <span class="tcgm-s2-select-arrow" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="#73777E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
        </div>

        <!-- Document upload zone -->
        <div class="tcgm-s2-field">
          <label class="tcgm-s2-label" for="tcgDocFile">
            Document <span class="tcgm-required">*</span>
          </label>
          <div
            class="tcgm-s2-upload-zone"
            [class.tcgm-s2-upload-zone--has-file]="!!newDocumentFile"
            (click)="triggerFileInput(docFileInput)"
            (keydown.enter)="triggerFileInput(docFileInput)"
            (keydown.space)="triggerFileInput(docFileInput)"
            role="button"
            tabindex="0"
            aria-label="Upload requirement document"
          >
            <input
              #docFileInput
              id="tcgDocFile"
              type="file"
              class="tcgm-s2-upload-hidden"
              [accept]="getAcceptedFormats(documentType)"
              (change)="onNewDocumentFileChange($event)"
            />
            <ng-container *ngIf="!newDocumentFile && !isUploadingDocument">
              <span class="tcgm-s2-upload-plus" aria-hidden="true">+</span>
              <span class="tcgm-s2-upload-text">{{ documentType ? getFormatHint(documentType) : 'Select a document type first' }}</span>
            </ng-container>
            <ng-container *ngIf="isUploadingDocument">
              <span class="tcgm-upload-spinner tcgm-upload-spinner--lg" aria-hidden="true"></span>
              <span class="tcgm-s2-upload-text">Uploading...</span>
            </ng-container>
            <span *ngIf="newDocumentFile && !isUploadingDocument" class="tcgm-s2-upload-filename">{{ newDocumentFile.name }}</span>
          </div>
          <!-- Upload error -->
          <p *ngIf="uploadError" class="tcgm-upload-error" role="alert">{{ uploadError }}</p>
          <!-- Format hint -->
          <p *ngIf="!uploadError && documentType" class="tcgm-upload-hint">
            Accepted: {{ getFormatHint(documentType) }}
          </p>
        </div>

      </div>

    </div>

    <div class="tcgm-footer">
      <span class="tcgm-step-label">{{ stepLabel }}</span>
      <div class="tcgm-actions">
        <button type="button" class="tcgm-btn-cancel" (click)="onBack()">Back</button>
        <button
          type="button"
          class="tcgm-btn-continue"
          [disabled]="!isStep2Valid || isAddingNewDocument"
          (click)="onContinue()"
        >Continue</button>
      </div>
    </div>

  </div>
</ng-container>

<!-- STEP 3 – What type of test cases would you like to generate? -->
<ng-container *ngIf="currentStep === 3">
  <div class="tcgm-s2-container">

    <div class="tcgm-s2-header">
      <h2 class="tcgm-s2-title">What type of test cases would you like to generate?</h2>
      <button type="button" class="tcgm-s2-close" aria-label="Close" (click)="onCancel()">
        <span class="tcgm-s2-close-icon" aria-hidden="true">&#x2715;</span>
      </button>
    </div>

    <div class="tcgm-s2-body">

      <!-- Functional Test Cases toggle -->
      <app-feature-toggle-card
        title="Functional Test Cases"
        description="Generate test cases to validate application functionality for smoke, sanity, or regression testing."
        [(enabled)]="functionalTestCases"
        (enabledChange)="onFunctionalToggleChange()"
      >
        <!-- Test Type radio group (shown only when toggle is ON) -->
        <div class="tcgm-radio-field">
          <label class="tcgm-s2-label">Test Type <span class="tcgm-required">*</span></label>
          <div class="tcgm-radio-group" role="radiogroup" aria-label="Test type">
            <label
              *ngFor="let t of testTypeOptions"
              class="tcgm-radio-option"
              [class.tcgm-radio-option--selected]="testType === t.value"
            >
              <input
                type="radio"
                class="tcgm-radio-input"
                [(ngModel)]="testType"
                [value]="t.value"
                name="testType"
              />
              <span class="tcgm-radio-dot"></span>
              <span class="tcgm-radio-label">{{ t.label }}</span>
            </label>
          </div>
        </div>
      </app-feature-toggle-card>

      <!-- Non-functional Test Cases toggle -->
      <app-feature-toggle-card
        title="Non-functional Test Cases"
        description="Generate test cases to validate application performance, security, usability, reliability, and other quality attributes"
        [(enabled)]="nonFunctionalTestCases"
        (enabledChange)="onNonFunctionalToggleChange()"
      ></app-feature-toggle-card>

    </div>

    <div class="tcgm-footer">
      <span class="tcgm-step-label">{{ stepLabel }}</span>
      <div class="tcgm-actions">
        <button type="button" class="tcgm-btn-cancel" (click)="onBack()">Back</button>
        <button
          type="button"
          class="tcgm-btn-continue"
          [disabled]="!isStep3Valid"
          (click)="onGenerate()"
        >Generate</button>
      </div>
    </div>

  </div>
</ng-container>

<!-- STEP 4 – Progress (phase stepper + process-logs-loader) -->
<ng-container *ngIf="currentStep === 4">
  <div class="tcgm-progress-wrapper">
    <div class="tcgm-s2-header">
      <h2 class="tcgm-s2-title">
        {{ progressPhase === 'uploading' ? 'Uploading Document' :
           progressPhase === 'kg'        ? 'Building Knowledge Graph' :
           progressPhase === 'done'      ? 'Complete!' :
           'Generating Test Cases' }}
      </h2>
      <button type="button" class="tcgm-s2-close" aria-label="Close" (click)="onCancel()">
        <span class="tcgm-s2-close-icon" aria-hidden="true">&#x2715;</span>
      </button>
    </div>

    <div class="tcgm-progress-body">
      <!-- Action Required banner (error state) -->
      <div class="tcgm-process-error-banner" *ngIf="isProgressError && progressText">
        <span class="tcgm-process-error-label">Action Required:</span>
        <span class="tcgm-process-error-message">{{ progressText }}</span>
      </div>

      <!-- Thinking timeline + gradient loader -->
      <app-process-logs-loader
        [logs]="processLogs"
        [statusLabel]="processStatusLabel"
        [isActive]="isProcessActive"
        [isError]="isProgressError"
        [showLoader]="true"
      ></app-process-logs-loader>
    </div>

    <div class="tcgm-footer" *ngIf="isProgressError">
      <span class="tcgm-step-label"></span>
      <div class="tcgm-actions">
        <button type="button" class="tcgm-btn-continue" (click)="onCancel()">Cancel</button>
      </div>
    </div>
  </div>
</ng-container>
